import {deepCopy, toasterError} from './index';
import {formatRate} from './formatter';

const REGEX = {
  email: /([a-zA-Z0-9_\.\-'])+\@(([a-zA-Z0-9\-])+\.)+([\w-]{2,24})+/,
  negativeNumber: /^-?(\d|,)*\.?\d+$/,
  number: /^(\d|,)*\.?\d+$/,
  phone: /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/,
  password: /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])([a-zA-Z0-9]{8,})/,
  quickbooksCharacters: /^((?!:|").)*$/,
};

export const formatRateField = rate =>
  rate !== null && rate.isDirty
    ? rate.value
    : (rate !== null && formatRate(rate.value)) || '';

export const numberRegex = fieldName => ({
  statement: REGEX.number,
  errorMessage: `The ${fieldName || 'field'} only supports positive numbers.`,
});

export const emailRegex = fieldName => ({
  statement: REGEX.email,
  errorMessage: `The ${fieldName || 'field'} must be a valid email address.`,
});

export const phoneRegex = fieldName => ({
  statement: REGEX.phone,
  errorMessage: `The ${fieldName || 'field'} has an invalid phone number`,
});

export const negativeNumberRegex = fieldName => ({
  statement: REGEX.negativeNumber,
  errorMessage: `The ${fieldName || 'field'} only supports numbers.`,
});

export const quickbooksCharactersRegex = fieldName => ({
  statement: REGEX.quickbooksCharacters,
  errorMessage: `The ${fieldName} field cannot contain unsupported characters:<br/><ul style='list-style-type:disc;list-style-position:inside;'><li>Colon (:)</li><li>Double quotation marks (\")</li></ul>`,
});

export const isNumber = value => REGEX.number.test(value);

export const isEmail = value => REGEX.email.test(value);

export const isPassword = value => REGEX.password.test(value);

export const setupPostBody = formObject => setupFormBody(formObject, false);

export const setupFormBody = (formObject, update = true) => {
  resetErrors(formObject);
  let returnObject = {
    body: update ? [] : {},
  };

  for (const key in formObject) {
    const property = formObject[key];

    if (!property || typeof property !== 'object') continue;

    if (
      property.isRequired &&
      (!property.value ||
        (Array.isArray(property.value) && property.value.length === 0) ||
        (typeof property.value === 'string' && property.value.trim() === '')) &&
      property.value !== 0
    ) {
      returnObject = {
        hasError: true,
        errorMessage: `The ${property.displayName || key} field is required.`,
      };
      property.error = true;
      break;
    }

    if (property.value && property.regex !== undefined) {
      let matchesRegex = true;
      if (!Array.isArray(property.value)) {
        matchesRegex = !property.regex.statement.test(
          typeof property.value === 'string'
            ? property.value.trim()
            : property.value,
        );
      } else {
        matchesRegex = property.value.find(
          val => !property.regex.statement.test(val),
        );
      }

      if (matchesRegex) {
        returnObject = {
          hasError: true,
          errorMessage: property.regex.errorMessage,
        };
        property.error = true;
        break;
      }
    }

    property.error = false;

    const newValue =
      typeof property.value === 'string'
        ? property.value.trim()
        : property.value;

    if (update) {
      if (!property.isDirty) continue;

      returnObject.body.push({
        op: 'replace',
        path: `/${key}`,
        value: newValue,
      });
    } else {
      returnObject.body[key] = newValue;
    }
  }

  return returnObject;
};

export const setupNewFormObject = (newFormObject, existingData) => {
  const returnObject = deepCopy(newFormObject);

  for (const key in existingData) {
    if (!returnObject[key]) {
      returnObject[key] = {value: existingData[key]};
    } else {
      returnObject[key].value = existingData[key];
    }
  }

  return returnObject;
};

export const updateFormObject = (formObject, field, value) => {
  const returnObject = deepCopy(formObject);

  if (returnObject[field]) {
    returnObject[field].value = value;
    returnObject[field].isDirty = true;
  } else {
    returnObject[field] = {
      value,
      isDirty: true,
    };
  }

  return returnObject;
};

export const multipleUpdates = (formObject, updates) => {
  const returnObject = deepCopy(formObject);

  updates.forEach(({field, value}) => {
    if (returnObject[field]) {
      returnObject[field].value = value;
      returnObject[field].isDirty = true;
    } else {
      returnObject[field] = {
        value,
        isDirty: true,
      };
    }
  });

  return returnObject;
};

export const addToFormObject = (formObject, properties, field) => {
  const returnObject = deepCopy(formObject);

  returnObject[field] = properties;

  return returnObject;
};

export const multipleAddToFormObject = (formObject, fields) => {
  const returnObject = deepCopy(formObject);

  fields.forEach(({field, properties}) => {
    returnObject[field] = properties;
  });

  return returnObject;
};

export const validForm = formObject => {
  let valid = true;

  for (const key in formObject) {
    const property = formObject[key];

    if (!property || typeof property !== 'object') continue;

    const missing =
      property.isRequired &&
      (!property.value ||
        (Array.isArray(property.value) && property.value.length === 0) ||
        (typeof property.value === 'string' && property.value.trim() === '')) &&
      property.value !== 0;

    const mismatch =
      property.value &&
      property.regex !== undefined &&
      !property.regex.statement.test(property.value.trim());

    if (missing || mismatch) {
      valid = false;

      break;
    }
  }

  return valid;
};

export const resetDirty = formObject => {
  for (const key in formObject) {
    if (typeof formObject[key] === 'object') {
      formObject[key].isDirty = false;
    }
  }
};

export const resetErrors = formObject => {
  for (const key in formObject) {
    if (typeof formObject[key] === 'object') {
      formObject[key].error = false;
    }
  }
};

export const requireNone = form => {
  const newObj = {};
  Object.keys(form).forEach(key => {
    newObj[key] = {...form[key], isRequired: false};
  });
  return newObj;
};

export const validateForm = (fields, patch = false) => {
  const formObject = setupFormBody(fields, patch);

  if (formObject.hasError) {
    toasterError(formObject.errorMessage);
    return null;
  }

  return formObject;
};



// WEBPACK FOOTER //
// ./src/helpers/formValidation.js